"use client";

// Push wiring for the dashboard shell.
//
// Owns three things:
//   1. Registering the FCM token with the API once permission already exists —
//      silently, on mount, so a manager who enabled push yesterday keeps
//      receiving alerts without touching Settings again.
//   2. Streaming foreground messages into the toast system and invalidating the
//      alert feed so the bell badge moves without a reload.
//   3. Exposing enable/disable to the permission component, and the current
//      token to AuthProvider so logout can unregister it.
//
// It never prompts on its own: the browser dialog is only opened from an
// explicit click in NotificationPermissionCard.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useRouter } from "next/navigation";
import { useToast } from "./Toast";
import { useAuth } from "./AuthProvider";
import { bumpNotificationRevision } from "../lib/notificationRevision";
import { isFirebaseConfigured } from "../lib/firebase";
import { setActivePushToken } from "../lib/pushTeardown";
import {
  clearRegistrationRecord,
  deleteFcmToken,
  getOrRefreshFcmToken,
  isPushSupported,
  listenForForegroundMessages,
  permissionState,
  registerTokenWithBackend,
  requestNotificationPermission,
  unregisterTokenFromBackend,
  type PermissionState,
  type TokenFailure,
} from "../lib/pushNotifications";

export interface PushCtx {
  /** Browser permission, or "unsupported" when FCM cannot run here. */
  permission: PermissionState;
  /** False when the deployment carries no Firebase web config. */
  configured: boolean;
  /** True once a token is registered with the API. */
  enabled: boolean;
  /** In-flight enable request. */
  busy: boolean;
  /** Why the last enable attempt failed, if it did. */
  failure: TokenFailure | null;
  /** Prompts (if needed), mints a token and registers it. */
  enable: () => Promise<void>;
  /** Unregisters this browser without signing out. */
  disable: () => Promise<void>;
}

const Ctx = createContext<PushCtx | null>(null);

// The Permissions API can watch notification state, but not in every browser and
// not for the FCM-relevant transitions we care about. Permission only changes
// here as the result of a prompt, so the provider re-renders explicitly instead
// of subscribing. The store exists purely to keep the SSR snapshot honest.
function subscribeNoop(): () => void {
  return () => {};
}

function permissionServerSnapshot(): PermissionState {
  return "default";
}

export default function PushProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const router = useRouter();

  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<TokenFailure | null>(null);

  const configured = isFirebaseConfigured();
  const userId = user?.id ?? null;

  // Notification.permission is browser state, not React state: read it through
  // an external store so the server snapshot stays "default" (the API does not
  // exist during SSR) and the client reads the real value after hydration.
  // `bump` re-reads it after a prompt resolves.
  const [, bump] = useState(0);
  const permission = useSyncExternalStore(
    subscribeNoop,
    permissionState,
    permissionServerSnapshot,
  );

  // Silent re-registration. Runs only when permission is already granted, so it
  // never triggers a prompt. Guarded against Strict Mode's double invoke by the
  // ref plus the session record inside registerTokenWithBackend.
  const attempted = useRef<string | null>(null);
  useEffect(() => {
    if (!configured) return;
    if (!userId) return;
    if (!isPushSupported()) return;
    if (permissionState() !== "granted") return;
    if (attempted.current === userId) return;
    attempted.current = userId;

    let active = true;
    void (async () => {
      const { token: fresh, reason } = await getOrRefreshFcmToken();
      if (!fresh) {
        // `attempted` is only a guard against concurrent duplicate work — clear
        // it so a later mount (or a retry from Settings) can try again.
        attempted.current = null;
        if (active) setFailure(reason ?? "token-failed");
        return;
      }

      // Deliberately NOT gated on `active`: registering the token is the whole
      // point of this effect, and in dev (Strict Mode's double invoke, Fast
      // Refresh) cleanup runs before this resolves. Bailing here would mint a
      // token and never POST it, leaving the browser silently un-targetable.
      // registerTokenWithBackend is idempotent, so running it after unmount is
      // safe.
      const ok = await registerTokenWithBackend(fresh, userId);
      setActivePushToken(fresh);

      // Only the React state updates are unmount-guarded.
      if (!active) return;
      // Hold the token even if the POST failed: logout should still try to
      // unregister whatever this browser was issued.
      setToken(fresh);
      if (!ok) setFailure("token-failed");
    })();

    return () => {
      active = false;
    };
  }, [configured, userId]);

  // Hand the token to the logout path (AuthProvider sits above this provider and
  // cannot read the context — see lib/pushTeardown).
  useEffect(() => {
    setActivePushToken(token);
  }, [token]);

  // Foreground messages -> toast + feed invalidation. Subscribing whenever
  // messaging is available (not only when registered) means a token minted in
  // another tab still surfaces alerts here.
  useEffect(() => {
    if (!configured || !userId || !isPushSupported()) return;

    return listenForForegroundMessages((payload) => {
      const data = payload.data ?? {};
      const note = payload.notification ?? {};
      const title = note.title ?? data.title ?? "New bell";
      const body = note.body ?? data.body;

      toast({
        title: body ? `${title} — ${body}` : title,
        tone: data.priority === "HIGH" ? "danger" : "accent",
        icon: "bell",
      });

      // Pull the authoritative row rather than trusting the payload.
      bumpNotificationRevision();

      // Warm the destination so clicking through is instant. Best-effort: a bad
      // route in the payload must not break message handling.
      const url = data.url ?? data.click_action;
      if (url && url.startsWith("/")) {
        try {
          router.prefetch(url);
        } catch {
          // Unknown route — the click still navigates normally.
        }
      }
    });
  }, [configured, userId, toast, router]);

  const enable = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    setFailure(null);
    try {
      const granted = await requestNotificationPermission();
      // Notification.permission has moved; re-read it for the UI.
      bump((n) => n + 1);
      if (granted !== "granted") {
        setFailure(granted === "unsupported" ? "unsupported" : "permission-denied");
        return;
      }

      const { token: fresh, reason } = await getOrRefreshFcmToken();
      if (!fresh) {
        setFailure(reason ?? "token-failed");
        return;
      }

      const ok = await registerTokenWithBackend(fresh, userId);
      setToken(fresh);
      if (!ok) {
        setFailure("token-failed");
        toast({ title: "Could not register this device", tone: "danger", icon: "bell" });
        return;
      }
      toast({ title: "Push notifications enabled", tone: "ok", icon: "bell" });
    } finally {
      setBusy(false);
    }
  }, [userId, toast]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      if (token) await unregisterTokenFromBackend(token);
      await deleteFcmToken();
      if (userId) clearRegistrationRecord(userId);
      // Allow the silent effect to run again if the user re-enables.
      attempted.current = null;
      setToken(null);
      toast({ title: "Push notifications disabled", tone: "ok", icon: "bell" });
    } finally {
      setBusy(false);
    }
  }, [token, userId, toast]);

  return (
    <Ctx.Provider
      value={{
        permission,
        configured,
        enabled: token !== null,
        busy,
        failure,
        enable,
        disable,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

/** Push state for UI. Returns null outside the provider so components rendered
 *  on unauthenticated routes can simply skip push affordances. */
export function usePushOptional(): PushCtx | null {
  return useContext(Ctx);
}

export function usePush(): PushCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePush must be used within PushProvider");
  return ctx;
}
